import React, { useState } from 'react';
import AiTrainerPersonaPicker from './AiTrainerPersonaPicker';
import { OPENAI_DISCLOSURE } from '../../constants/aiTrainerPersonalities';
import { commonTimeZoneOptions, detectBrowserTimeZone } from '../../utils/aiTrainerApi';
import {
	aiTrainerCardClassName,
	aiTrainerHeadingClassName,
	aiTrainerInputClassName,
	aiTrainerMutedTextClassName,
	aiTrainerPrimaryButtonClassName,
	aiTrainerSecondaryButtonClassName
} from './aiTrainerStyles';

const STEPS = ['welcome', 'personality', 'timezone_nudges', 'confirm'];

function StatusBadge({ children, lightClassName }) {
	return (
		<span
			className={`rounded-full px-3 py-1 text-sm ${lightClassName} dark:bg-dark-bg dark:bg-opacity-80`}
		>
			{children}
		</span>
	);
}

export default function AiTrainerSetupWizard({
	profileName,
	contextSnapshot,
	onComplete,
	saving,
	error
}) {
	const [stepIndex, setStepIndex] = useState(0);
	const [personality, setPersonality] = useState('accountability_partner');
	const [nudgesEnabled, setNudgesEnabled] = useState(true);
	const [timeZone, setTimeZone] = useState(detectBrowserTimeZone());

	const step = STEPS[stepIndex];
	const activeCount = contextSnapshot?.active_challenges?.length || 0;
	const pendingCount = contextSnapshot?.pending_challenges?.length || 0;
	const goalsCount = contextSnapshot?.active_goals?.length || 0;

	function nextStep() {
		setStepIndex((current) => Math.min(current + 1, STEPS.length - 1));
	}

	function prevStep() {
		setStepIndex((current) => Math.max(current - 1, 0));
	}

	function handleLaunch() {
		onComplete({
			ai_trainer_enabled: true,
			ai_trainer_personality: personality,
			ai_trainer_nudges_enabled: nudgesEnabled,
			time_zone: timeZone
		});
	}

	return (
		<div className={`mx-auto max-w-3xl ${aiTrainerCardClassName} p-6`}>
			{step === 'welcome' && (
				<div>
					<h2 className={`text-2xl font-bold ${aiTrainerHeadingClassName}`}>
						Meet your AI trainer{profileName ? `, ${profileName}` : ''}
					</h2>
					<p className={`mt-2 ${aiTrainerMutedTextClassName}`}>
						A science-backed coach that knows your goals, challenges, and habits — and reaches out
						before you have to ask.
					</p>
					<div className="mt-4 flex flex-wrap gap-2">
						{goalsCount > 0 && (
							<StatusBadge lightClassName="bg-blue-100 text-blue-800 dark:text-blue-300">
								{goalsCount} active goal{goalsCount === 1 ? '' : 's'}
							</StatusBadge>
						)}
						{activeCount > 0 && (
							<StatusBadge lightClassName="bg-green-100 text-green-800 dark:text-green-300">
								{activeCount} active challenge{activeCount === 1 ? '' : 's'}
							</StatusBadge>
						)}
						{pendingCount > 0 && (
							<StatusBadge lightClassName="bg-yellow-100 text-yellow-800 dark:text-yellow-300">
								{pendingCount} pending
							</StatusBadge>
						)}
					</div>
					<button type="button" onClick={nextStep} className={`mt-6 ${aiTrainerPrimaryButtonClassName}`}>
						Continue
					</button>
				</div>
			)}

			{step === 'personality' && (
				<div>
					<h2 className={`text-xl font-bold ${aiTrainerHeadingClassName}`}>Pick your coach vibe</h2>
					<p className={`mt-1 ${aiTrainerMutedTextClassName}`}>Required — you can change this later.</p>
					<div className="mt-4">
						<AiTrainerPersonaPicker value={personality} onChange={setPersonality} />
					</div>
					<div className="mt-6 flex justify-end gap-3">
						<button type="button" onClick={prevStep} className={aiTrainerSecondaryButtonClassName}>
							Back
						</button>
						<button
							type="button"
							onClick={nextStep}
							disabled={!personality}
							className={aiTrainerPrimaryButtonClassName}
						>
							Continue
						</button>
					</div>
				</div>
			)}

			{step === 'timezone_nudges' && (
				<div>
					<h2 className={`text-xl font-bold ${aiTrainerHeadingClassName}`}>Time zone & nudges</h2>
					<p className={`mt-1 ${aiTrainerMutedTextClassName}`}>
						Your coach uses local time for morning briefings and deadline reminders.
					</p>
					<label className={`mt-4 block text-sm font-medium ${aiTrainerHeadingClassName}`}>
						Time zone
						<select
							value={timeZone}
							onChange={(event) => setTimeZone(event.target.value)}
							className={`mt-1 ${aiTrainerInputClassName}`}
						>
							{commonTimeZoneOptions().map((zone) => (
								<option key={zone} value={zone}>
									{zone.replace('_', ' ')}
								</option>
							))}
						</select>
					</label>
					<label className={`mt-4 flex items-center gap-2 text-sm ${aiTrainerHeadingClassName}`}>
						<input
							type="checkbox"
							checked={nudgesEnabled}
							onChange={(event) => setNudgesEnabled(event.target.checked)}
						/>
						Send proactive reminders (max ~2/day)
					</label>
					<p className={`mt-4 text-xs ${aiTrainerMutedTextClassName}`}>{OPENAI_DISCLOSURE}</p>
					<div className="mt-6 flex justify-end gap-3">
						<button type="button" onClick={prevStep} className={aiTrainerSecondaryButtonClassName}>
							Back
						</button>
						<button type="button" onClick={nextStep} className={aiTrainerPrimaryButtonClassName}>
							Continue
						</button>
					</div>
				</div>
			)}

			{step === 'confirm' && (
				<div>
					<h2 className={`text-xl font-bold ${aiTrainerHeadingClassName}`}>Ready to train</h2>
					<ul className={`mt-4 space-y-2 text-sm ${aiTrainerMutedTextClassName}`}>
						<li>Personality: {personality.replace(/_/g, ' ')}</li>
						<li>Time zone: {timeZone}</li>
						<li>Proactive nudges: {nudgesEnabled ? 'On' : 'Off'}</li>
					</ul>
					{error && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{error}</p>}
					<div className="mt-6 flex justify-end gap-3">
						<button type="button" onClick={prevStep} className={aiTrainerSecondaryButtonClassName}>
							Back
						</button>
						<button
							type="button"
							onClick={handleLaunch}
							disabled={saving}
							className={aiTrainerPrimaryButtonClassName}
						>
							{saving ? 'Starting…' : 'Start training'}
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
